'use client';
import { useEffect, useMemo, useState } from 'react';
import parse from 'html-react-parser';
import AxiosInstance, { AxiosRequestConfig } from 'axios';
import { OptionTypes } from 'types';
import { useProducts } from 'stores/product';
import { Specification } from 'stores/configurator';
import { formatValue } from 'utils/formatNumber';
import {
  abbreviations,
  compareObj,
  downloads,
  exclusions,
  formatKey,
} from 'utils/specificationHelper';
import Heading from 'components/common/Heading';
import { ProductCompareItem } from 'components/product/ProductCompareItem';
import {
  ProductCompareSelectors,
  ProductState,
  ProductType,
} from 'components/product/ProductCompareSelectors';
import { useStore } from 'zustand';
import { useMapStore } from '@/stores/map/mapStore';
import ModalMap from '@/components/map/component/modal-map';
import React from 'react';
import FormatValue from '@/utils/FormatValue';
import { MRCOOL } from '@/utils/constants';

interface Props {
  params: Promise<{
    slug: string;
  }>;
  searchParams: any;
}

export default function Page({ params }: Props) {
  const { modalMapOpen, setModalMapOpen } = useStore(useMapStore, (state) => ({
    modalMapOpen: state.modalMapOpen,
    setModalMapOpen: state.setModalMapOpen,
  }));
  const resolvedSlug = React.use(params).slug;
  const slugTitle = FormatValue.capitalizeFirstLetterOfEachWord(
    resolvedSlug.replace('-', ' ')
  );
  const { product, getProduct } = useProducts();
  const [seriesItems, setSeriesItems] = useState<OptionTypes[] | any[]>([]);
  const [selectedModel1, setSelectedModel1] = useState<OptionTypes | any>(null);
  const [selectedModel2, setSelectedModel2] = useState<OptionTypes | any>(null);
  const [productItem, setProductItem] = useState<
    Record<ProductType, ProductState>
  >({
    product1: {
      specs: {},
      hasDownloads: false,
      hasLoaded: false,
    },
    product2: {
      specs: {},
      hasDownloads: false,
      hasLoaded: false,
    },
  });

  const handleProductItem = (slot: ProductType, product: Specification) => {
    const productKeys = Object.keys(product);

    setProductItem((prev) => ({
      ...prev,
      [slot]: {
        specs: product,
        hasLoaded: !!productKeys.length,
        hasDownloads: productKeys.some((key) => downloads.includes(key)),
      },
    }));
  };

  // set model-product by series
  useEffect(() => {
    const axios = AxiosInstance.create({
      baseURL: process.env.API_ENDPOINT,
    });
    // get model-product by series
    const getProductByModelSeries = async (
      series: string,
      params?: AxiosRequestConfig
    ) => {
      const diy_params_series = ['diy_c', 'diy_c_mz'],
        filterDIY = 'DIY OuttaSight',
        prefix = 'configurator';

      let option: OptionTypes[] | any[] = [];
      try {
        const { data, status } = await axios.get(
          `${prefix}/model-by-series/${series}`,
          { params }
        );
        if (status == 200) {
          if (data?.results?.other && data?.results?.other?.length > 0) {
            data?.results?.other?.map((item: any) => {
              option.push({
                ...item,
                label: `${item?.model} - ${item?.series}`,
                value: item?.model,
              });
            });
          }
          const filter = diy_params_series?.some((item) => item == series);
          if (filter) {
            setSeriesItems(option?.filter((item) => item?.series == filterDIY));
          } else {
            setSeriesItems(option);
          }
        } else {
          throw Error;
        }
      } catch (error) {
        console.error('Error get product by series no :', error);
      }
    };

    const series = product?.attributes?.modelSeries;

    if (series) {
      getProductByModelSeries(series);
    }
  }, [product]);

  // set select model
  useEffect(() => {
    if (seriesItems?.length > 0) {
      if (seriesItems?.length >= 2) {
        setSelectedModel2(seriesItems[1]);
      } else {
        setSelectedModel2(null);
      }
      setSelectedModel1(seriesItems[0]);
    } else {
      setSelectedModel1(null);
      setSelectedModel2(null);
    }
  }, [seriesItems]);

  // option model1
  const optionModel1 = useMemo(() => {
    let option: OptionTypes[] | any[] = seriesItems?.filter(
      (item) => item?.model !== selectedModel2?.value
    );
    return option;
  }, [seriesItems, selectedModel2]);

  // option model2
  const optionModel2 = useMemo(() => {
    let option: OptionTypes[] | any[] = seriesItems?.filter(
      (item) => item?.model !== selectedModel1?.value
    );
    return option;
  }, [seriesItems, selectedModel1]);

  const handleDownloadsForSlot = (slot: ProductType) => {
    return Object.entries(
      compareObj(productItem[slot].specs || {}, productItem[slot].specs || {})
    ).map(([key, value]: any) => {
      return !downloads.includes(key) ? null : (
        <p key={key}>
          <a
            href={value.left}
            title={formatKey(key, abbreviations)}
            target='_blank'
          >
            {formatKey(key, abbreviations)}
          </a>
        </p>
      );
    });
  };

  // get product by slug
  useEffect(() => {
    getProduct({
      id: resolvedSlug,
    });
  }, [resolvedSlug, slugTitle, getProduct]);
  useEffect(() => {
    document.title = `${product?.attributes?.compare_meta_title ? `${product.attributes.compare_meta_title} | ${MRCOOL}` : MRCOOL}`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute(
        'content',
        `${product?.attributes?.compare_meta_description ? `${product.attributes.compare_meta_description}` : 'Meta Description'}`
      );
    }
  }, [product]);
  return (
    <section className='relative min-h-screen w-full bg-white pt-48'>
      <div className='container mx-auto flex flex-col items-stretch justify-start gap-y-16 px-5'>
        <div className='mx-auto flex w-full flex-col items-center justify-center gap-y-9 md:w-10/12'>
          <Heading size={2}>Compare models</Heading>
        </div>

        {/* seletor */}
        <div className='grid w-full grid-cols-1 gap-4 sm:gap-9 lg:grid-cols-6'>
          <div className='grid w-full grid-cols-2 gap-9 lg:col-start-2 lg:col-end-6'>
            <div className='h-full w-full sm:min-h-[250px]'>
              <ProductCompareSelectors
                options={optionModel1}
                selectedModel={selectedModel1}
                setSelectedModel={setSelectedModel1}
                product={productItem.product1}
                setProduct={(value) => handleProductItem('product1', value)}
              />
            </div>

            <div className='h-full w-full sm:min-h-[250px]'>
              <ProductCompareSelectors
                options={optionModel2}
                selectedModel={selectedModel2}
                setSelectedModel={setSelectedModel2}
                product={productItem.product2}
                setProduct={(value) => handleProductItem('product2', value)}
              />
            </div>
          </div>
        </div>

        {/* product-selected */}
        <div className='flex w-full flex-col items-stretch justify-start gap-0'>
          {Object.entries(
            compareObj(
              productItem.product1.specs || {},
              productItem.product2.specs || {}
            )
          ).map(([key, value]: any) => {
            if (exclusions.includes(key)) {
              return null;
            }
            return (
              <ProductCompareItem
                key={key}
                title={formatKey(key, abbreviations)}
                productSlotLeft={
                  key === 'msrp'
                    ? `$${formatValue(value?.left || 0)}`
                    : parse(value.left || '-')
                }
                productSlotRight={
                  key === 'msrp'
                    ? `$${formatValue(value?.right || 0)}`
                    : parse(value.right || '-')
                }
                loadingLeft={!productItem.product1.hasLoaded}
                loadingRight={!productItem.product2.hasLoaded}
              />
            );
          })}

          {(productItem.product1.hasDownloads ||
            productItem.product2.hasDownloads) && (
            <ProductCompareItem
              key={'download'}
              title={formatKey('Download', abbreviations)}
              productSlotLeft={handleDownloadsForSlot('product1')}
              productSlotRight={handleDownloadsForSlot('product2')}
              loadingLeft={!productItem.product1.hasLoaded}
              loadingRight={!productItem.product1.hasLoaded}
            />
          )}
        </div>
      </div>
      <ModalMap mapOpen={modalMapOpen} setMapOpen={setModalMapOpen} />
    </section>
  );
}
